import { useCallback, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useUserSettingsContext } from 'contexts';
import { SpeakerTestButton } from 'features/SpeakerTest/SpeakerTestButton';
import { VolumeSlider } from 'features/VolumeSlider/components/VolumeSlider/VolumeSlider';
import { selectRoom, selectIsPreJoin } from 'state/selectors';
import { Section } from '../../components/Section/Section';
import { Selector } from '../../components/Selector/Selector';
import { getAudioDevicesOptions, getActiveAudioDevice } from '../../helper';
import { SpeakerTestButtonWrapper, VolumeSliderWrapper } from './view';

type DeviceOption = {
    label: string;
    value: string;
};

export const SpeakerSection = observer(() => {
    const { t } = useTranslation();
    const room = selectRoom();
    const isPreJoin = selectIsPreJoin();

    const { audioOutputDevices, audioOutputDeviceId, setAudioOutputDeviceId } = useUserSettingsContext();

    const [isDisabled, setIsDisabled] = useState<boolean>(false);

    const options = useMemo(() => getAudioDevicesOptions({ devices: audioOutputDevices }), [audioOutputDevices]);

    const value = useMemo(() => {
        const active = getActiveAudioDevice({ devices: audioOutputDevices, currentDeviceId: audioOutputDeviceId });
        return active ? { label: active.label, value: active.deviceId } : null;
    }, [audioOutputDevices, audioOutputDeviceId]);

    const onChange = useCallback(
        (speaker: DeviceOption) => {
            if (isPreJoin) {
                setAudioOutputDeviceId(speaker.value);
                return;
            }

            if (!room) return;

            setIsDisabled(true);
            room.switchActiveDevice('audiooutput', speaker.value)
                .then(() => {
                    setAudioOutputDeviceId(speaker.value);
                })
                .catch((e) => {
                    console.warn('Error switching audiooutput device', e);
                })
                .finally(() => {
                    setIsDisabled(false);
                });
        },
        [room, isPreJoin],
    );

    return (
        <Section title={t('vks_microphone_audio_output')} withSeparatorBefore>
            <Selector
                isSearchable={false}
                closeMenuOnSelect={false}
                options={options}
                value={value}
                isDisabled={isDisabled}
                onChange={onChange}
                width={'100%'}
                cy={'speakersSelect'}
            />
            <SpeakerTestButtonWrapper>
                <SpeakerTestButton withDescription={true} isTransparent={false} />
            </SpeakerTestButtonWrapper>
            <VolumeSliderWrapper>
                <VolumeSlider />
            </VolumeSliderWrapper>
        </Section>
    );
});

SpeakerSection.displayName = 'SpeakerSection';
